import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_3e66b0ed-93a2-4dc7-abca-ce9b0d15b317/artifacts/su7kwk53_StartEase%20Logo.png';

const GoogleCallbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(true);

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const errorParam = searchParams.get('error');

      if (errorParam) {
        setError('Google login was cancelled or failed');
        setProcessing(false);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        setProcessing(false);
        return;
      }

      try {
        const redirectUri = `${window.location.origin}/auth/google/callback`;
        const response = await axios.post(`${API}/auth/google/callback`, {
          code,
          redirect_uri: redirectUri
        });

        const { token, user } = response.data;
        
        // Login and redirect
        login(token, user);
        
        // Redirect based on user role
        if (user?.role === 'super_admin' || user?.role === 'admin') {
          navigate('/superadmin');
        } else {
          navigate('/dashboard');
        }
      } catch (err) {
        console.error('Google OAuth error:', err);
        setError(err.response?.data?.detail || 'Failed to complete Google login');
        setProcessing(false);
      }
    };

    handleCallback();
  }, [searchParams, login, navigate]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0b1310' }}>
        <div className="w-full max-w-[420px] text-center">
          <div className="flex justify-center mb-6">
            <img src={LOGO_URL} alt="StartEase Agent" className="w-16 h-16" />
          </div>
          <div 
            className="rounded-xl p-8"
            style={{ 
              backgroundColor: '#111827',
              border: '1px solid #1f2937'
            }}
          >
            <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Login Failed</h2>
            <p className="text-gray-400 mb-6">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 rounded-lg font-medium text-black"
              style={{ backgroundColor: '#059669' }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#0b1310' }}>
      <div className="w-full max-w-[420px] text-center">
        <div className="flex justify-center mb-6">
          <img src={LOGO_URL} alt="StartEase Agent" className="w-16 h-16" />
        </div>
        <div 
          className="rounded-xl p-8"
          style={{ 
            backgroundColor: '#111827',
            border: '1px solid #1f2937'
          }}
        >
          <div className="flex justify-center mb-4">
            <svg className="animate-spin h-10 w-10 text-[#059669]" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Signing you in...</h2>
          <p className="text-gray-400">Please wait while we complete your Google login</p>
        </div>
      </div>
    </div>
  );
};

export default GoogleCallbackPage;
